<script setup>
// Aba "Agendas dos usuarios" (spec 011): expediente padrao das agendas de usuario e, por usuario, expediente proprio e
// servicos. A agenda de cada usuario nasce na primeira gravacao; quem ja e profissional edita no profissional.
/* global axios */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useModulePermissions } from 'dashboard/composables/useModulePermissions';
import AModal from './AModal.vue';
import WorkingHoursEditor from './WorkingHoursEditor.vue';
import { DAYS } from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;
const { moduleCan } = useModulePermissions();
const canEdit = computed(() => moduleCan('update', 'appointments'));

const users = ref([]);
const services = ref([]);
const defaultHours = ref([]);
const savingDefault = ref(false);

const show = ref(false);
const form = ref({});
const ownHours = ref(false);
const hours = ref([]);
const sel = ref([]);
const saving = ref(false);

const load = async () => {
  const { data } = (await axios.get(`${base}/user_agendas`)).data;
  users.value = data.users || [];
  defaultHours.value = data.default_working_hours || [];
  services.value =
    (await axios.get(`${base}/appointment_services`)).data.data || [];
};

const saveDefault = async () => {
  savingDefault.value = true;
  try {
    await axios.patch(`${base}/appointment_config`, {
      user_agenda_working_hours: defaultHours.value,
    });
    useAlert(t('ATENDIMENTOS.userAgendas.saved'));
    await load();
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('ATENDIMENTOS.userAgendas.error')
    );
  } finally {
    savingDefault.value = false;
  }
};

const open = user => {
  form.value = { ...user };
  ownHours.value = !user.inherits_working_hours;
  hours.value = (user.working_hours || []).map(w => ({
    day: w.day,
    startTime: w.startTime,
    endTime: w.endTime,
  }));
  sel.value = [...(user.service_ids || [])];
  show.value = true;
};

const save = async () => {
  saving.value = true;
  try {
    await axios.patch(`${base}/user_agendas/${form.value.user_id}`, {
      working_hours: ownHours.value ? hours.value : null,
      service_ids: sel.value,
    });
    show.value = false;
    useAlert(t('ATENDIMENTOS.userAgendas.saved'));
    await load();
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('ATENDIMENTOS.userAgendas.error')
    );
  } finally {
    saving.value = false;
  }
};

const toggleSvc = id => {
  const i = sel.value.indexOf(id);
  if (i >= 0) sel.value.splice(i, 1);
  else sel.value.push(id);
};
const svcName = id => services.value.find(s => s.id === id)?.name;
const daysSummary = list =>
  (list || [])
    .map(w => DAYS.find(([, d]) => d === w.day))
    .filter(Boolean)
    .map(([label]) => t(label))
    .join(', ');

onMounted(load);
</script>

<template>
  <div class="p-6 space-y-6" data-test="user-agendas-tab">
    <section
      class="p-4 border rounded-xl border-n-weak bg-n-solid-2"
      data-test="default-hours"
    >
      <h3 class="mb-1 text-base font-semibold text-n-slate-12">
        {{ t('ATENDIMENTOS.userAgendas.defaultTitle') }}
      </h3>
      <p class="mb-3 text-sm text-n-slate-10">
        {{ t('ATENDIMENTOS.userAgendas.defaultHint') }}
      </p>
      <WorkingHoursEditor v-model="defaultHours" />
      <div v-if="canEdit" class="flex justify-end mt-3">
        <button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          :disabled="savingDefault || !defaultHours.length"
          data-test="save-default"
          @click="saveDefault"
        >
          {{ t('ATENDIMENTOS.userAgendas.saveDefault') }}
        </button>
      </div>
    </section>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="user in users"
        :key="user.user_id"
        class="p-4 border rounded-xl border-n-weak bg-n-solid-2"
        :data-test="`user-${user.user_id}`"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="font-semibold truncate text-n-slate-12">
              {{ user.name }}
            </div>
            <div
              v-if="user.practitioner_id"
              class="text-xs text-n-slate-10"
              data-test="linked"
            >
              {{
                t('ATENDIMENTOS.userAgendas.linked', {
                  name: user.practitioner_name,
                })
              }}
            </div>
            <div v-else class="text-xs text-n-slate-10">
              {{
                user.inherits_working_hours
                  ? t('ATENDIMENTOS.userAgendas.usesDefault')
                  : t('ATENDIMENTOS.userAgendas.ownHours')
              }}
              · {{ daysSummary(user.working_hours) }}
            </div>
          </div>
          <button
            v-if="canEdit && !user.practitioner_id"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            data-test="edit"
            @click="open(user)"
          >
            {{ t('ATENDIMENTOS.practitioners.edit') }}
          </button>
        </div>
        <div v-if="user.google_connected" class="mt-2 text-xs text-n-teal-11">
          {{ t('AGENDA_SCOPES.GOOGLE_CONNECTED') }}
        </div>
        <div class="flex flex-wrap gap-1.5 mt-3">
          <span
            v-for="sid in user.service_ids || []"
            :key="sid"
            class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-1 text-n-slate-11"
            >{{ svcName(sid) }}</span
          >
        </div>
      </div>
    </div>

    <AModal
      v-if="show"
      :title="t('ATENDIMENTOS.userAgendas.modalTitle', { name: form.name })"
      @close="show = false"
    >
      <div class="space-y-2">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="ownHours" type="radio" :value="false" data-test="use-default" />
          {{ t('ATENDIMENTOS.userAgendas.useDefault') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="ownHours" type="radio" :value="true" data-test="use-own" />
          {{ t('ATENDIMENTOS.userAgendas.useOwn') }}
        </label>
      </div>
      <WorkingHoursEditor v-if="ownHours" v-model="hours" />
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.enabledServices')
        }}</label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="s in services"
            :key="s.id"
            type="button"
            class="px-2 py-1 text-xs rounded-full border"
            :class="
              sel.includes(s.id)
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-background text-n-slate-11 border-n-weak'
            "
            @click="toggleSvc(s.id)"
          >
            {{ s.name }}
          </button>
        </div>
        <p class="mt-1 text-[0.6875rem] text-n-slate-10">
          {{ t('ATENDIMENTOS.userAgendas.servicesHint') }}
        </p>
      </div>
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.practitioners.modal.cancel') }}</button
        ><button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          :disabled="saving || (ownHours && !hours.length)"
          data-test="save-user"
          @click="save"
        >
          {{ t('ATENDIMENTOS.practitioners.modal.save') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
